import React, { useState, useEffect, FC, useContext } from 'react';
import {
  alpha,
  Box,
  CircularProgress,
  createTheme,
  hexToRgb,
  useTheme,
  ThemeProvider,
  CssBaseline,
  LinearProgress,
  Typography,
} from '@mui/material';
import SurveyFooter from '../../components/SurveyFooter/SurveyFooter';
import SurveyService from '../../services/SurveyService';
import { ISurvey } from '../../shared/interfaces/ISurvey';
import SurveyHeader from '../../components/SurveyHeader/SurveyHeader';
import Section from '../../components/Main/Section/Section';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../providers/CurrentViewProvider';
import MoodBarometerContent from '../../components/MoodBarometerContent/MoodBarometerContent';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';
import {
  scopeSurveyCustomCss,
  SURVEY_CUSTOM_CSS_SCOPE_CLASS,
  SURVEY_CUSTOM_CSS_SCOPE_SELECTOR,
} from '../../shared/SurveyCustomCss';

interface Props {
  previewSurvey?: ISurvey & {
    shouldShowPrivacyNotice: boolean;
    privacyNoticeText: string;
    privacyNoticeLink: string;
  };
}

const MoodBarometerPage: FC<Props> = ({ previewSurvey }) => {
  const { t } = useTranslation();
  const { setCurrentView } = useContext(CurrentViewContext);
  const theme = useTheme();

  const [moodBarometerPage, setMoodBarometerPage] = useState<
    | (ISurvey & {
        shouldShowPrivacyNotice: boolean;
        privacyNoticeText: string;
        privacyNoticeLink: string;
      })
    | null
  >(previewSurvey ?? null);
  const [currentId, setCurrentId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const localTranslation = useLocalTranslation(
    moodBarometerPage ? moodBarometerPage : { lang: 'de' }
  );

  const getFontFormatByFileExtension = (fontFormat: FontFileExtensions) => {
    return {
      woff2: 'woff2',
      woff: 'woff',
      ttf: 'truetype',
    }[fontFormat];
  };

  useEffect(() => {
    if (previewSurvey) return;
    const controller = new AbortController();
    const splitUrl = window.location.href.split('/');
    const extractValue = splitUrl[splitUrl.length - 1].toString();

    const getSurveyData = async () => {
      try {
        const response = await SurveyService.getSurveyPublic(
          extractValue,
          'trend',
          setLoading,
          controller.signal
        );
        if (response) {
          setCurrentId(response.id);
          setMoodBarometerPage(response);
        } else {
          setError(true);
        }
      } catch (error) {
        console.error('Fehler beim Abrufen der Umfrage:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    getSurveyData();

    return () => {
      controller.abort();
    };
  }, [currentId, previewSurvey]);

  useEffect(() => {
    setCurrentView(t('mood_barometer'));
    document.title = moodBarometerPage?.label
      ? moodBarometerPage?.label
      : `${t('app_branding')} ${t('mood_barometer')}`;
  }, [setCurrentView, t, moodBarometerPage]);

  if (moodBarometerPage) {
    const effectiveFontFamily =
      moodBarometerPage.customFont && moodBarometerPage.customFontFormat
        ? `"${moodBarometerPage.id}"`
        : '"Arial", "Helvetica", sans-serif';
    const scopedCustomCss = scopeSurveyCustomCss(moodBarometerPage.customCss);
    const customFontCss =
      moodBarometerPage.customFont && moodBarometerPage.customFontFormat
        ? `
            @font-face {
              font-family: "${moodBarometerPage.id}";
              src: url(${moodBarometerPage.customFont}) format("${getFontFormatByFileExtension(
            moodBarometerPage.customFontFormat as FontFileExtensions
          )}");
            }

            ${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR},
            ${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR} * {
              font-family: ${effectiveFontFamily} !important;
            }
          `
        : `
            ${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR},
            ${SURVEY_CUSTOM_CSS_SCOPE_SELECTOR} * {
              font-family: ${effectiveFontFamily} !important;
            }
          `;
    const customTheme = createTheme({
      palette: {
        primary: {
          main: moodBarometerPage.primaryColor
            ? moodBarometerPage.primaryColor
            : theme.palette.primary.main,
        },
        divider: moodBarometerPage.secondaryColor
          ? alpha(hexToRgb(moodBarometerPage.secondaryColor), 0.2)
          : theme.palette.divider,
      },
      typography: {
        fontFamily: effectiveFontFamily,
      },
    });

    return (
      <LocalTranslationContext.Provider value={localTranslation}>
        <CssBaseline />
        <Box
          className="survey-page-shell survey-page-shell--trend"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100%',
            backgroundColor: '#ffffff',
          }}
        >
          <ThemeProvider theme={customTheme}>
            <style>{customFontCss}</style>
            {scopedCustomCss && <style>{scopedCustomCss}</style>}
            {loading && <LinearProgress />}
            <Box
              className={SURVEY_CUSTOM_CSS_SCOPE_CLASS}
              sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}
            >
              <Box
                className="survey-page survey-page--trend trend-page-root"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '100%',
                  flex: 1,
                  fontFamily: effectiveFontFamily,
                }}
              >
                <SurveyHeader
                  logoLink={moodBarometerPage.logoLink}
                  logo={moodBarometerPage.companyLogo}
                />
                <MoodBarometerContent {...moodBarometerPage} />
              </Box>
            </Box>
          </ThemeProvider>

          <SurveyFooter
            company={moodBarometerPage.companyName}
            privacyPolicyLink={moodBarometerPage.privacyPolicyLink}
            imprintLink={moodBarometerPage.imprintLink}
          />
        </Box>
      </LocalTranslationContext.Provider>
    );
  } else {
    return (
      <Section>
        <Box
          sx={{
            display: 'flex',
            alignSelf: 'center',
            alignItems: 'center',
            flex: 1,
          }}
        >
          {error ? (
            <Typography>{t('survey_not_found_or_archived')}</Typography>
          ) : (
            <CircularProgress />
          )}
        </Box>
      </Section>
    );
  }
};

export default MoodBarometerPage;
